import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, AlertCircle, AlertTriangle, Shield } from "lucide-react";
import { browseIngredient } from "../lib/api";
import { cn } from "../lib/utils";

const RISK_TONE = {
  HIGH: "bg-rose-50 text-rose-700 border-rose-200",
  MODERATE: "bg-amber-50 text-amber-800 border-amber-200",
  LOW: "bg-lime-50 text-lime-700 border-lime-200",
};
const RISK_ICON = { HIGH: AlertCircle, MODERATE: AlertTriangle, LOW: Shield };
const RISK_HEADING = {
  HIGH: "Medications that should not be combined with this",
  MODERATE: "Medications to use this ingredient carefully around",
  LOW: "Medications where this is generally fine",
};

function GroupBlock({ level, rows, ingId }) {
  if (!rows?.length) return null;
  const Icon = RISK_ICON[level];
  return (
    <section
      data-testid={`browse-ing-group-${level}`}
      className="mb-8"
    >
      <div className={cn(
        "inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[12px] font-semibold uppercase tracking-wide mb-4",
        RISK_TONE[level],
      )}>
        <Icon className="w-3.5 h-3.5" />
        {level} — {rows.length} medication{rows.length === 1 ? "" : "s"}
      </div>
      <p className="text-sm text-ink-soft mb-4">{RISK_HEADING[level]}</p>
      <ul className="space-y-2">
        {rows.map((r) => {
          const med = r.medication || {};
          return (
            <li key={r.id} className="rounded-xl border border-surface-line bg-white p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[260px]">
                  <Link
                    to={`/browse/med/${med.id}`}
                    data-testid={`browse-ing-medication-${med.id}`}
                    className="font-semibold text-ink hover:underline"
                  >
                    {med.name || med.generic_name}
                  </Link>
                  {med.category && (
                    <span className="ml-2 text-[12px] text-ink-mute">{med.category}</span>
                  )}
                  {r.summary && <p className="mt-1 text-sm text-ink-soft">{r.summary}</p>}
                  {r.recommendation && (
                    <p className="mt-1 text-sm text-ink"><b>What to do:</b> {r.recommendation}</p>
                  )}
                </div>
                <Link
                  to={`/browse/pair/${med.id}/${ingId}`}
                  className="inline-flex items-center gap-1 text-sm text-ink-soft hover:text-ink"
                >
                  Details <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function BrowseIngredient() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await browseIngredient(id);
        setData(res);
      } catch (e) {
        setErr(e?.response?.status === 404 ? "Not found" : "Could not load");
      }
    })();
  }, [id]);

  if (err) {
    return (
      <main className="max-w-screen-lg mx-auto px-4 sm:px-6 md:px-10 py-16">
        <p className="text-ink-soft">{err}</p>
        <Link to="/browse" className="text-ink underline">← Back to browse</Link>
      </main>
    );
  }
  if (!data) return <main className="max-w-screen-lg mx-auto px-4 sm:px-6 md:px-10 py-16 text-ink-mute">Loading…</main>;

  const ing = data.ingredient;
  return (
    <main data-testid="browse-ingredient-page" className="max-w-screen-lg mx-auto px-4 sm:px-6 md:px-10 py-10 sm:py-14">
      <button
        data-testid="browse-back"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-[13px] text-ink-mute hover:text-ink mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <header className="mb-8 pb-6 border-b border-surface-line">
        <p className="text-[11px] uppercase tracking-[0.16em] text-ink-mute mb-2">
          Ingredient
        </p>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-ink">
          {ing.name}
        </h1>
        {ing.inci_name && ing.inci_name !== ing.name && (
          <p className="mt-2 text-sm text-ink-mute">INCI: {ing.inci_name}</p>
        )}
        {ing.category && (
          <p className="mt-2 text-sm text-ink-soft">{ing.category}</p>
        )}
        {ing.aliases?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {ing.aliases.slice(0, 12).map((a) => (
              <span key={a} className="text-[11px] px-2 py-0.5 rounded-full bg-cream-100 border border-surface-line text-ink-soft">
                {a}
              </span>
            ))}
          </div>
        )}
        <div
          data-testid="browse-ing-counts"
          className="mt-5 flex flex-wrap gap-x-6 gap-y-1 text-[13px] text-ink-mute"
        >
          <span><b className="text-ink">{data.counts.HIGH || 0}</b> high risk</span>
          <span><b className="text-ink">{data.counts.MODERATE || 0}</b> moderate</span>
          <span><b className="text-ink">{data.counts.LOW || 0}</b> low</span>
        </div>
      </header>

      <GroupBlock level="HIGH" rows={data.groups.HIGH} ingId={id} />
      <GroupBlock level="MODERATE" rows={data.groups.MODERATE} ingId={id} />
      <GroupBlock level="LOW" rows={data.groups.LOW} ingId={id} />

      {data.total === 0 && (
        <p className="text-ink-mute">No tracked interactions for this ingredient yet.</p>
      )}
    </main>
  );
}
